/**
 * PSDImporter — Extracts layers from a PSD file and packs them into a tileset grid.
 *
 * Uses ag-psd to parse PSD in the browser. Extracts each visible leaf layer,
 * detects a common tile size (or uses user-specified), and packs into a
 * grid-aligned spritesheet canvas that can be loaded as a tileset.
 *
 * Designed for game asset packs where artists provide layered PSDs with
 * individual tiles/sprites on separate layers.
 */

// Lazy-loaded to avoid bundling ag-psd (~300KB) into the main chunk.
// Only pulled in when a user actually selects a PSD file.
let _readPsd = null;
async function getReadPsd() {
  if (!_readPsd) {
    const mod = await import('ag-psd');
    _readPsd = mod.readPsd;
  }
  return _readPsd;
}

/**
 * Parse a PSD file and extract layer images.
 * @param {ArrayBuffer} buffer — Raw PSD file data
 * @returns {{ layers: Array<{name, canvas, width, height, left, top}>, width, height }}
 */
export async function extractLayers(buffer) {
  const readPsd = await getReadPsd();
  const psd = readPsd(new Uint8Array(buffer), {
    skipCompositeImageData: true,
    skipThumbnail: true,
  });

  const layers = [];
  _collectLayers(psd.children || [], layers);

  return {
    layers,
    psdWidth: psd.width,
    psdHeight: psd.height,
  };
}

function _collectLayers(children, out) {
  for (const layer of children) {
    // Recurse into groups
    if (layer.children) {
      _collectLayers(layer.children, out);
      continue;
    }

    // Skip hidden layers
    if (layer.hidden) continue;

    // Skip layers without image data
    if (!layer.canvas) continue;

    out.push({
      name: layer.name || 'Untitled',
      canvas: layer.canvas,
      width: layer.canvas.width,
      height: layer.canvas.height,
      left: layer.left || 0,
      top: layer.top || 0,
    });
  }
}

/**
 * Detect the most common tile size from a set of layers.
 * Looks for the most frequent width/height dimensions.
 * @param {Array} layers — from extractLayers()
 * @returns {{ width: number, height: number }}
 */
export function detectTileSize(layers) {
  if (layers.length === 0) return { width: 32, height: 32 };

  // Count dimension frequencies
  const widthCounts = new Map();
  const heightCounts = new Map();

  for (const layer of layers) {
    widthCounts.set(layer.width, (widthCounts.get(layer.width) || 0) + 1);
    heightCounts.set(layer.height, (heightCounts.get(layer.height) || 0) + 1);
  }

  // Find the most common width and height
  let bestWidth = 32, bestWidthCount = 0;
  for (const [w, count] of widthCounts) {
    if (count > bestWidthCount) { bestWidth = w; bestWidthCount = count; }
  }

  let bestHeight = 32, bestHeightCount = 0;
  for (const [h, count] of heightCounts) {
    if (count > bestHeightCount) { bestHeight = h; bestHeightCount = count; }
  }

  // If no clear winner, try common tile sizes
  const commonSizes = [16, 32, 48, 64];
  if (bestWidthCount < layers.length * 0.3) {
    // Less than 30% agreement — check if most layers are multiples of a common size
    for (const size of commonSizes) {
      const multiples = layers.filter(l => l.width % size === 0 && l.height % size === 0).length;
      if (multiples > layers.length * 0.5) {
        bestWidth = size;
        bestHeight = size;
        break;
      }
    }
  }

  return { width: bestWidth, height: bestHeight };
}

/**
 * Pack extracted layers into a grid-aligned tileset spritesheet.
 *
 * @param {Array} layers — from extractLayers()
 * @param {number} tileWidth — target tile width
 * @param {number} tileHeight — target tile height
 * @param {Object} [options]
 * @param {number} [options.maxColumns=16] — max columns in the grid
 * @param {boolean} [options.fitToTile=true] — resize/crop layers to tile size
 * @param {function} [options.onProgress] — callback(percent, message)
 * @returns {{ canvas: HTMLCanvasElement, columns: number, rows: number, tileCount: number, skipped: number }}
 */
export function packIntoGrid(layers, tileWidth, tileHeight, options = {}) {
  const maxColumns = options.maxColumns || 16;
  const fitToTile = options.fitToTile !== false;
  const onProgress = options.onProgress || (() => {});

  // Filter and prepare tiles
  const tiles = [];
  let skipped = 0;

  for (const layer of layers) {
    if (fitToTile) {
      // Layers that are exactly tile-sized or smaller: include directly
      // Layers that are multiples: split into grid cells
      // Layers that are odd sizes: center-crop or scale

      if (layer.width === tileWidth && layer.height === tileHeight) {
        // Exact match — use directly
        tiles.push(layer.canvas);
      } else if (layer.width >= tileWidth && layer.height >= tileHeight
                 && layer.width % tileWidth === 0 && layer.height % tileHeight === 0) {
        // Multiple of tile size — split into individual tiles
        const cols = layer.width / tileWidth;
        const rows = layer.height / tileHeight;
        for (let row = 0; row < rows; row++) {
          for (let col = 0; col < cols; col++) {
            const cell = document.createElement('canvas');
            cell.width = tileWidth;
            cell.height = tileHeight;
            const ctx = cell.getContext('2d');
            ctx.drawImage(layer.canvas,
              col * tileWidth, row * tileHeight, tileWidth, tileHeight,
              0, 0, tileWidth, tileHeight);
            // Skip fully transparent tiles
            if (!_isBlank(ctx, tileWidth, tileHeight)) {
              tiles.push(cell);
            }
          }
        }
      } else if (layer.width <= tileWidth * 2 && layer.height <= tileHeight * 2) {
        // Small layer — center it on a tile-sized canvas
        const cell = document.createElement('canvas');
        cell.width = tileWidth;
        cell.height = tileHeight;
        const ctx = cell.getContext('2d');
        const ox = Math.floor((tileWidth - layer.width) / 2);
        const oy = Math.floor((tileHeight - layer.height) / 2);
        ctx.drawImage(layer.canvas, ox, oy);
        if (!_isBlank(ctx, tileWidth, tileHeight)) {
          tiles.push(cell);
        }
      } else {
        // Too large and not a clean multiple — scale down to fit
        const scale = Math.min(tileWidth / layer.width, tileHeight / layer.height);
        const sw = Math.round(layer.width * scale);
        const sh = Math.round(layer.height * scale);
        const cell = document.createElement('canvas');
        cell.width = tileWidth;
        cell.height = tileHeight;
        const ctx = cell.getContext('2d');
        ctx.imageSmoothingEnabled = false;
        const ox = Math.floor((tileWidth - sw) / 2);
        const oy = Math.floor((tileHeight - sh) / 2);
        ctx.drawImage(layer.canvas, 0, 0, layer.width, layer.height, ox, oy, sw, sh);
        tiles.push(cell);
      }
    } else {
      tiles.push(layer.canvas);
    }
  }

  if (tiles.length === 0) {
    // Return a 1×1 transparent tile if nothing was extracted
    const empty = document.createElement('canvas');
    empty.width = tileWidth;
    empty.height = tileHeight;
    return { canvas: empty, columns: 1, rows: 1, tileCount: 0, skipped: layers.length };
  }

  // Calculate grid dimensions
  const columns = Math.min(maxColumns, tiles.length);
  const rows = Math.ceil(tiles.length / columns);

  // Create output canvas
  const output = document.createElement('canvas');
  output.width = columns * tileWidth;
  output.height = rows * tileHeight;
  const ctx = output.getContext('2d');
  ctx.imageSmoothingEnabled = false;

  // Draw tiles into grid
  for (let i = 0; i < tiles.length; i++) {
    const col = i % columns;
    const row = Math.floor(i / columns);
    ctx.drawImage(tiles[i], col * tileWidth, row * tileHeight);

    if (i % 50 === 0) {
      onProgress(Math.round((i / tiles.length) * 100), `Packing tile ${i + 1}/${tiles.length}`);
    }
  }

  onProgress(100, 'Done');

  return {
    canvas: output,
    columns,
    rows,
    tileCount: tiles.length,
    skipped,
  };
}

/** Check if a canvas tile is fully transparent (blank). */
function _isBlank(ctx, w, h) {
  const data = ctx.getImageData(0, 0, w, h).data;
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] > 0) return false;
  }
  return true;
}

/**
 * Full PSD → tileset pipeline.
 * @param {ArrayBuffer} buffer — Raw PSD file data
 * @param {Object} [options]
 * @param {number} [options.tileWidth] — override tile width (auto-detect if omitted)
 * @param {number} [options.tileHeight] — override tile height (auto-detect if omitted)
 * @param {number} [options.maxColumns=16]
 * @param {function} [options.onProgress] — callback(percent, message)
 * @returns {{ canvas, columns, rows, tileCount, skipped, detectedSize }}
 */
export async function importPSD(buffer, options = {}) {
  const onProgress = options.onProgress || (() => {});

  onProgress(10, 'Reading PSD file...');
  const { layers } = await extractLayers(buffer);

  onProgress(30, `Found ${layers.length} layers`);

  const detectedSize = detectTileSize(layers);
  const tileWidth = options.tileWidth || detectedSize.width;
  const tileHeight = options.tileHeight || detectedSize.height;

  onProgress(40, `Packing with ${tileWidth}×${tileHeight} tile size...`);

  const result = packIntoGrid(layers, tileWidth, tileHeight, {
    maxColumns: options.maxColumns || 16,
    onProgress: (pct, msg) => onProgress(40 + Math.round(pct * 0.6), msg),
  });

  return { ...result, detectedSize };
}
